import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { getActiveOrganization } from "selectors/organizations";
import { useSelector } from "react-redux";
import Countries from "const/Countries";
import DateFormats from "const/DateFormats";
import DatePickerLocalized from "nlib/common/DatePickerLocalized";
import DateTimeFormats from "const/DateTimeFormats";
import Dates from "utils/Dates";
import Input from "nlib/ui/Input";
import Platforms from "utils/Platforms";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import moment from "moment";

const EU_REGEXP = [
  /(?<d>\d{2})[.,:; -](?<m>\d{2})[.,:; -](?<y>\d{4})/,
  /(?<y>\d{4})[.,:; -](?<m>\d{2})[.,:; -](?<d>\d{2})/
];

const US_REGEXP = [
  /(?<m>\d{2})\/?(?<d>\d{2})\/?(?<y>\d{4})/,
  /(?<d>\d{2})[.,:; -](?<m>\d{2})[.,:; -](?<y>\d{4})/,
  /(?<y>\d{4})[.,:; -](?<m>\d{2})[.,:; -](?<d>\d{2})/
];

const parseInputValue = (value, usFormat, monthOnly) => {
  if (!value) return null;
  if (monthOnly) {
    const parsed = moment(value, DateTimeFormats.MONTH_AND_YEAR_TEXT, true);

    return parsed.isValid() ? parsed.format(DateTimeFormats.API_MONTH_DATE) : null;
  }

  const match = (usFormat ? US_REGEXP : EU_REGEXP).map((regex) => value.match(regex)).find(Boolean);

  if (!match) return null;

  const { y: yy, m: mm, d: dd } = {
    ...match.groups,
    d: monthOnly ? "01" : match.groups.d
  };

  const result = `${yy}-${mm}-${dd}`;

  return Date.parse(result) ? result : null;
};

const DatePickerInput = (props) => {
  const {
    value,
    disabled,
    placeholder,
    error,
    invalid,
    valid,
    focused,
    active,
    readOnly,
    openToDate,
    autoFocus = false,
    className,
    monthOnly,
    onFocus,
    onBlur,
    onChange,
    ...restProps
  } = props;

  const [inputValue, setInputValue] = useState("");

  const [opened, setOpened] = useState(false);

  const activeOrganization = useSelector(getActiveOrganization);

  const usFormat = activeOrganization.countryCode === Countries.US;

  const dateFormat = monthOnly ? DateTimeFormats.MONTH_AND_YEAR_TEXT : (usFormat ? DateFormats.US : DateFormats.EU);

  const setInputValueFormatted = useCallback((string) => {
    setInputValue(string ? moment(string).format(dateFormat) : "");
  }, [dateFormat]);

  const handleInputChange = useCallback((newValue) => {
    setInputValue(newValue);

    const parsed = parseInputValue(newValue, usFormat, monthOnly);

    if (parsed) {
      onChange(parsed);
      setOpened(false);
    } else if (!newValue) onChange("");
  }, [monthOnly, usFormat, onChange, setOpened]);

  const handleClearClick = useCallback(() => {
    setInputValue("");
    onChange("");
  }, [onChange]);

  const handleInputFocus = useCallback((event) => {
    if (Platforms.checkIsTouchDevice()) event.target.blur();
    if (onFocus) onFocus(event);
  }, [onFocus]);

  const handleInputBlur = useCallback((event) => {
    const parsed = parseInputValue(inputValue, usFormat, monthOnly);

    if (!parsed && inputValue.trim()) onChange("");
    if (parsed) setInputValueFormatted(parsed);
    if (onBlur) onBlur(event);
  }, [inputValue, usFormat, monthOnly, onBlur, onChange, setInputValueFormatted]);

  useEffect(() => {
    setInputValueFormatted(value || "");
  }, [value, setInputValueFormatted]);
  useEffect(() => {
    if (autoFocus) setOpened(autoFocus);
  }, [autoFocus]);

  const toggleButton = useMemo(() => {
    return (
      <>
        <Input
          placeholder={placeholder}
          value={inputValue}
          focused={focused}
          disabled={disabled}
          active={active}
          valid={valid}
          error={error}
          invalid={invalid}
          readOnly={readOnly}
          iconBefore={Icons.CalendarBlank}
          autoFocus={autoFocus}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onChange={handleInputChange} />
        {!!value && !readOnly && (
          <span
            className={Css.clear}
            onClick={handleClearClick}>
            <Icons.X />
          </span>
        )}
      </>
    );
  }, [
    focused,
    disabled,
    active,
    value,
    error,
    invalid,
    valid,
    readOnly,
    inputValue,
    placeholder,
    autoFocus,
    handleClearClick,
    handleInputFocus,
    handleInputChange,
    handleInputBlur
  ]);

  return (
    <DatePickerLocalized
      {...restProps}
      monthOnly={monthOnly}
      value={value || (openToDate ? Dates.formatApiDate(openToDate) : value)}
      opened={opened}
      disabled={disabled}
      readOnly={readOnly}
      className={classNames(Css.datePickerInput, className)}
      toggleButton={toggleButton}
      onChange={onChange}
      setOpened={setOpened} />
  );
};

export default React.memo(DatePickerInput);
